import { useEffect, useState } from 'react';
import { IslamicEvent } from '../utils/islamicEvents';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { TimeAndPrayerDisplay } from './TimeAndPrayerDisplay';
import { PrayerTime } from '../types/prayer';

interface DailyContent {
  day_number: number;
  task_title: string;
  task_description: string;
  hadith_text: string;
  hadith_reference: string;
  quran_surah: string;
  quran_reference: string;
}

interface ActiveEventDailyProps {
  event: IslamicEvent;
  dayOfEvent: number;
  totalDays: number;
  prayers: PrayerTime[];
}

type SlideType = 'overview' | 'task' | 'hadith' | 'quran';

export function ActiveEventDaily({ event, dayOfEvent, totalDays, prayers }: ActiveEventDailyProps) {
  const [dailyContent, setDailyContent] = useState<DailyContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState<SlideType>('overview');
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const { language } = useLanguage();

  const minSwipeDistance = 50;

  const getSlides = (): SlideType[] => {
    return dailyContent ? ['overview', 'task', 'hadith', 'quran'] : ['overview'];
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const currentX = e.targetTouches[0].clientX;
    const currentY = e.targetTouches[0].clientY;

    setTouchEnd({
      x: currentX,
      y: currentY
    });

    const diffX = Math.abs(touchStart.x - currentX);
    const diffY = Math.abs(touchStart.y - currentY);

    if (diffX > diffY && diffX > 10) {
      e.preventDefault();
    }
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;

    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);

    if (!isHorizontalSwipe || Math.abs(distanceX) < minSwipeDistance) return;

    const isLeftSwipe = distanceX > 0;
    const isRightSwipe = distanceX < 0;

    const slides = getSlides();
    const currentIndex = slides.indexOf(currentSlide);

    if (isLeftSwipe && currentIndex < slides.length - 1) {
      setCurrentSlide(slides[currentIndex + 1]);
    }

    if (isRightSwipe && currentIndex > 0) {
      setCurrentSlide(slides[currentIndex - 1]);
    }
  };

  useEffect(() => {
    async function fetchDailyContent() {
      setLoading(true);
      const { data } = await supabase
        .from('islamic_event_daily_content')
        .select('*')
        .eq('event_name', event.name.replace(' Begins', ''))
        .eq('day_number', dayOfEvent)
        .maybeSingle();

      if (data) {
        setDailyContent(data);
      }
      setLoading(false);
    }

    fetchDailyContent();
  }, [event.name, dayOfEvent]);

  useEffect(() => {
    const slides: SlideType[] = dailyContent
      ? ['overview', 'task', 'hadith', 'quran']
      : ['overview'];

    let currentIndex = 0;
    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % slides.length;
      setCurrentSlide(slides[currentIndex]);
    }, 15000);

    return () => clearInterval(interval);
  }, [dailyContent]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-teal-600 to-green-700 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white/30 border-t-white mx-auto"></div>
          <p className="text-white text-2xl font-semibold">
            {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  const eventName = event.name.replace(' Begins', '');
  const progressPercentage = (dayOfEvent / totalDays) * 100;

  const prayerList = prayers.map(p => ({
    name: p.name,
    time: p.time,
    key: p.key
  }));

  const getEventEmoji = (name: string) => {
    if (name.includes('Ramadan')) return '🌙';
    if (name.includes('Hajj')) return '🕋';
    if (name.includes("Eid al-Fitr")) return '🌟';
    if (name.includes("Eid al-Adha")) return '🐑';
    if (name.includes('Muharram')) return '📅';
    if (name.includes('Ashura')) return '🕌';
    return '✨';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-600 via-teal-600 to-green-700 relative overflow-y-auto overflow-x-hidden">
      <TimeAndPrayerDisplay prayers={prayerList} />

      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-10 left-10 text-9xl">🕌</div>
        <div className="absolute bottom-10 right-10 text-9xl">☪️</div>
        <div className="absolute top-1/2 left-1/4 text-7xl">✨</div>
        <div className="absolute top-1/4 right-1/4 text-7xl">📿</div>
      </div>

      <div
        className="relative min-h-screen flex items-center justify-center p-3 md:p-6 pb-20 pt-36 md:pt-40"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="w-full max-w-6xl px-2 sm:px-4">
          {currentSlide === 'overview' && (
            <div className="bg-white/95 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-16 shadow-2xl border-t-4 border-teal-400 animate-fade-in w-full">
              <div className="text-center space-y-2 sm:space-y-3">
                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-amber-500 mb-2">
                  ﷽
                </div>

                <div className="text-5xl sm:text-6xl md:text-7xl mb-2 sm:mb-3 animate-pulse">
                  {getEventEmoji(eventName)}
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-gray-800 px-2">
                  {eventName}
                </h1>

                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-amber-600 mb-2 px-2" style={{ direction: 'rtl' }}>
                  {event.nameArabic}
                </div>

                <div className="border-t-2 border-gray-200 my-4 sm:my-6"></div>

                <div className="text-xl sm:text-2xl md:text-3xl font-semibold mb-2 text-teal-700">
                  Day {dayOfEvent} of {totalDays}
                </div>

                <div className="text-xl sm:text-2xl md:text-3xl font-arabic text-teal-600 mb-3 sm:mb-4" style={{ direction: 'rtl' }}>
                  اليوم {dayOfEvent} من {totalDays}
                </div>

                <div className="max-w-2xl mx-auto space-y-3 mt-4 sm:mt-6 px-2">
                  <div className="bg-gray-200 backdrop-blur-md rounded-full h-6 sm:h-8 overflow-hidden shadow-2xl">
                    <div
                      className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 h-full transition-all duration-1000 flex items-center justify-center text-emerald-900 font-bold text-sm sm:text-lg"
                      style={{ width: `${progressPercentage}%` }}
                    >
                      {Math.round(progressPercentage)}%
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 sm:gap-3 mt-6 sm:mt-8">
                  <div className="h-1 w-12 sm:w-20 bg-gradient-to-r from-transparent via-teal-400 to-teal-400"></div>
                  <div className="text-2xl sm:text-3xl text-teal-600">۞</div>
                  <div className="h-1 w-12 sm:w-20 bg-gradient-to-l from-transparent via-teal-400 to-teal-400"></div>
                </div>
                <div className="text-lg sm:text-xl md:text-2xl text-gray-700 mt-4 sm:mt-6 px-2">
                  May Allah accept from us and from you
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl font-arabic text-teal-600 mt-2 sm:mt-3 px-2" style={{ direction: 'rtl' }}>
                  تقبل الله منا ومنكم
                </div>
              </div>
            </div>
          )}

          {currentSlide === 'task' && dailyContent && (
            <div className="bg-white/95 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-16 shadow-2xl border-t-4 border-emerald-400 animate-fade-in w-full">
              <div className="text-center space-y-3 sm:space-y-4">
                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-amber-500 mb-2">
                  ﷽
                </div>

                <div className="text-5xl sm:text-6xl md:text-7xl mb-2 sm:mb-3">
                  🎯
                </div>

                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-emerald-700 mb-2 px-2">
                  Today's Task
                </h2>
                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-emerald-600 mb-3 sm:mb-4 px-2" style={{ direction: 'rtl' }}>
                  مهمة اليوم
                </div>

                <div className="border-t-2 border-gray-200 my-4 sm:my-6"></div>

                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-emerald-800 mb-4 sm:mb-6 leading-relaxed px-2">
                  {dailyContent.task_title}
                </h3>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-800 leading-relaxed mb-4 sm:mb-6 max-w-3xl mx-auto px-2">
                  {dailyContent.task_description}
                </p>
                <div className="flex items-center justify-center gap-3 sm:gap-4 mt-6 sm:mt-8">
                  <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-transparent via-emerald-400 to-emerald-400"></div>
                  <div className="text-3xl sm:text-4xl text-emerald-600">۞</div>
                  <div className="h-1 w-16 sm:w-24 bg-gradient-to-l from-transparent via-emerald-400 to-emerald-400"></div>
                </div>
              </div>
            </div>
          )}

          {currentSlide === 'hadith' && dailyContent && (
            <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-16 shadow-2xl border-t-4 border-amber-400 animate-fade-in w-full">
              <div className="text-center space-y-3 sm:space-y-4">
                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-amber-600 mb-2">
                  ﷽
                </div>

                <div className="text-5xl sm:text-6xl md:text-7xl mb-2 sm:mb-3">
                  📖
                </div>

                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-amber-900 mb-2 px-2">
                  Hadith of the Day
                </h2>
                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-amber-700 mb-3 sm:mb-4 px-2" style={{ direction: 'rtl' }}>
                  حديث اليوم
                </div>

                <div className="border-t-2 border-amber-300 my-4 sm:my-6"></div>

                <div className="text-5xl sm:text-6xl mb-4 sm:mb-6 text-amber-600">☪️</div>
                <p className="text-lg sm:text-xl md:text-3xl text-gray-900 leading-relaxed italic font-serif mb-4 sm:mb-6 max-w-4xl mx-auto px-2">
                  "{dailyContent.hadith_text}"
                </p>
                <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-transparent via-amber-500 to-amber-500"></div>
                  <div className="text-3xl sm:text-4xl text-amber-600">۞</div>
                  <div className="h-1 w-16 sm:w-24 bg-gradient-to-l from-transparent via-amber-500 to-amber-500"></div>
                </div>
                <p className="text-xl sm:text-2xl md:text-3xl text-amber-900 font-semibold px-2">
                  {dailyContent.hadith_reference}
                </p>
              </div>
            </div>
          )}

          {currentSlide === 'quran' && dailyContent && (
            <div className="bg-gradient-to-br from-teal-50 via-emerald-50 to-green-100 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-16 shadow-2xl border-t-4 border-emerald-400 animate-fade-in w-full">
              <div className="text-center space-y-3 sm:space-y-4">
                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-emerald-700 mb-2">
                  ﷽
                </div>

                <div className="text-5xl sm:text-6xl md:text-7xl mb-2 sm:mb-3">
                  📗
                </div>

                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-emerald-900 mb-2 px-2">
                  Quranic Reading
                </h2>
                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-emerald-700 mb-3 sm:mb-4 px-2" style={{ direction: 'rtl' }}>
                  قراءة قرآنية
                </div>

                <div className="border-t-2 border-emerald-300 my-4 sm:my-6"></div>

                <div className="text-5xl sm:text-6xl mb-4 sm:mb-6 text-emerald-600">🕌</div>
                <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-emerald-800 mb-3 sm:mb-4 px-2">
                  Surah {dailyContent.quran_surah}
                </h3>
                <div className="text-2xl sm:text-3xl md:text-4xl font-arabic text-emerald-700 mb-4 sm:mb-6 px-2" style={{ direction: 'rtl' }}>
                  سورة {dailyContent.quran_surah}
                </div>
                <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-transparent via-emerald-500 to-emerald-500"></div>
                  <div className="text-3xl sm:text-4xl text-emerald-600">۞</div>
                  <div className="h-1 w-16 sm:w-24 bg-gradient-to-l from-transparent via-emerald-500 to-emerald-500"></div>
                </div>
                <p className="text-lg sm:text-xl md:text-2xl text-gray-800 leading-relaxed mb-4 sm:mb-6 max-w-3xl mx-auto px-2">
                  {dailyContent.quran_reference}
                </p>
                <div className="mt-6 sm:mt-8 text-lg sm:text-xl md:text-2xl text-emerald-800 font-semibold px-2">
                  Take time to reflect on the words of Allah
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl font-arabic text-emerald-700 mt-2 sm:mt-3 px-2" style={{ direction: 'rtl' }}>
                  خذ وقتك للتأمل في كلمات الله
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center gap-4 sm:gap-6 mt-8 sm:mt-12">
            {/* Left Arrow */}
            <button
              onClick={() => {
                const slides = getSlides();
                const currentIndex = slides.indexOf(currentSlide);
                if (currentIndex > 0) setCurrentSlide(slides[currentIndex - 1]);
              }}
              disabled={getSlides().indexOf(currentSlide) === 0}
              className={`p-3 rounded-full backdrop-blur-md transition-all text-2xl sm:text-3xl ${
                getSlides().indexOf(currentSlide) === 0
                  ? 'bg-white/10 text-white/30 cursor-not-allowed'
                  : 'bg-white/20 text-white hover:bg-white/40 active:scale-95'
              }`}
              aria-label="Previous slide"
            >
              ←
            </button>

            {/* Slide Indicators */}
            <div className="flex gap-2 sm:gap-3">
              {getSlides().map((slide) => (
                <button
                  key={slide}
                  onClick={() => setCurrentSlide(slide as SlideType)}
                  className={`h-3 sm:h-4 rounded-full transition-all duration-300 ${
                    currentSlide === slide
                      ? 'w-10 sm:w-14 bg-white shadow-lg'
                      : 'w-3 sm:w-4 bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Go to ${slide} slide`}
                />
              ))}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => {
                const slides = getSlides();
                const currentIndex = slides.indexOf(currentSlide);
                if (currentIndex < slides.length - 1) setCurrentSlide(slides[currentIndex + 1]);
              }}
              disabled={getSlides().indexOf(currentSlide) === getSlides().length - 1}
              className={`p-3 rounded-full backdrop-blur-md transition-all text-2xl sm:text-3xl ${
                getSlides().indexOf(currentSlide) === getSlides().length - 1
                  ? 'bg-white/10 text-white/30 cursor-not-allowed'
                  : 'bg-white/20 text-white hover:bg-white/40 active:scale-95'
              }`}
              aria-label="Next slide"
            >
              →
            </button>
          </div>

          {/* Swipe hint for mobile */}
          <div className="text-center mt-4 text-white/70 text-base sm:text-lg font-semibold backdrop-blur-sm bg-white/10 rounded-full py-2 px-6 inline-block">
            ← Swipe to navigate →
          </div>
        </div>
      </div>
    </div>
  );
}
